// ADR 7.10.26w §4 — where the open app card lives, and why it left the header.
//
// The card used to be `HeaderApps`' local state, rendered inside `Header`. The
// companion page draws it from a cell of `ShellBody`, and a body cell cannot
// draw state the header owns (8.9.26i §4's argument, again), so the open entry
// lives in a provider `CompanionProviders` mounts above both.
//
// **Focus on close is the provider's.** `useModalDialog` records the focused
// element when its dialog mounts; after a page → sheet move that is no longer
// the Apps button, because the page frame was torn down in the same commit.
// So the opener is recorded here at `show`, and a passive effect on the
// `entry → null` edge re-focuses it while it is still connected. React runs
// it after the dialog's own cleanup, so it has the last word.
//
// This module imports no wasm: it is on the boot graph (25.9.26f).

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from 'react'

import type { AppSlot } from '../../../wasm'
import type { AppInstall } from '../installsApi'

/** One Apps-menu row: the web's own install item and the slot the card draws it as. */
export interface BarEntry {
  install: AppInstall
  slot: AppSlot
}

export interface AppCardState {
  open: BarEntry | null
  /** Records the focused element (the Apps button) as the opener, then opens `entry`. */
  show(entry: BarEntry): void
  close(): void
}

const NO_CARD: AppCardState = { open: null, show: () => {}, close: () => {} }

/** Defaulted, not throwing, as `useTutorDock` is: some suites mount a header alone. */
const AppCardContext = createContext<AppCardState>(NO_CARD)

export function useAppCardState(): AppCardState {
  return useContext(AppCardContext)
}

/** Re-focus the opener on the `entry → null` edge, if it is still on screen. */
function useFocusReturn(open: BarEntry | null, opener: MutableRefObject<Element | null>) {
  const wasOpen = useRef(false)
  useEffect(() => {
    const closing = wasOpen.current && open === null
    wasOpen.current = open !== null
    if (!closing) return
    const target = opener.current
    opener.current = null
    if (target instanceof HTMLElement && target.isConnected) target.focus()
  }, [open, opener])
}

export function AppCardProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<BarEntry | null>(null)
  const opener = useRef<Element | null>(null)
  useFocusReturn(open, opener)

  const show = useCallback((entry: BarEntry) => {
    opener.current = document.activeElement
    setOpen(entry)
  }, [])
  const close = useCallback(() => setOpen(null), [])
  const value = useMemo<AppCardState>(() => ({ open, show, close }), [open, show, close])
  return <AppCardContext.Provider value={value}>{children}</AppCardContext.Provider>
}
